import { useState, type FormEvent } from "react";
import { site } from "../../config/site";
import { sendMagicLink } from "../lib/auth-client";
import { linkProps } from "../lib/router";
import { NarrowPage, Notice } from "../components/Layout";

/** Stashed until the guest lands back signed in, then used to prefill onboarding. */
export const PENDING_KEY = "wedding:pending-profile";

export interface PendingProfile {
  fullName?: string;
  phone?: string;
}

export function readPendingProfile(): PendingProfile {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) ?? "{}");
  } catch {
    return {};
  }
}

export function clearPendingProfile() {
  localStorage.removeItem(PENDING_KEY);
}

function SentConfirmation({ email, onBack }: { email: string; onBack: () => void }) {
  return (
    <div className="animate-rise text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-sage-soft">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6f7f63" strokeWidth="1.5">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="m2 7 10 6 10-6" />
        </svg>
      </div>
      <h1 className="mt-6 font-display text-4xl font-light">Check your inbox</h1>
      <p className="mt-4 leading-relaxed text-muted">
        We sent a sign-in link to <span className="text-ink">{email}</span>. It's good for the next
        15 minutes.
      </p>
      <p className="mt-8 text-sm text-muted">
        Nothing arrived? Check spam, or{" "}
        <button onClick={onBack} className="text-ink underline decoration-line underline-offset-4">
          try a different address
        </button>
        .
      </p>
    </div>
  );
}

function AuthForm({
  mode,
}: {
  mode: "signup" | "login";
}) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isSignup = mode === "signup";

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!email.trim()) return setError("We need your email to send the link.");
    if (isSignup && !fullName.trim()) return setError("Please tell us your name.");

    setBusy(true);
    // Held locally so the magic-link round trip doesn't lose what they typed.
    if (isSignup) {
      localStorage.setItem(PENDING_KEY, JSON.stringify({ fullName: fullName.trim(), phone: phone.trim() }));
    }
    const err = await sendMagicLink(email);
    setBusy(false);

    if (err) setError(err);
    else setSent(true);
  }

  if (sent) return <SentConfirmation email={email} onBack={() => setSent(false)} />;

  return (
    <div>
      <p className="eyebrow">{isSignup ? "RSVP" : "Welcome back"}</p>
      <h1 className="mt-4 font-display text-4xl leading-tight font-light sm:text-5xl">
        {isSignup ? "Let's find your invitation" : "Sign in"}
      </h1>
      <p className="mt-4 leading-relaxed text-muted">
        {isSignup
          ? "Tell us who you are and we'll show you which events you're invited to. No password — we'll email you a link."
          : "Enter the email you signed up with and we'll send you a fresh sign-in link."}
      </p>

      <form onSubmit={onSubmit} className="mt-10 space-y-4">
        {isSignup && (
          <div>
            <label htmlFor="name" className="eyebrow mb-2 block">
              Full name
            </label>
            <input
              id="name"
              className="field"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jordan Rivera"
              autoComplete="name"
            />
          </div>
        )}

        <div>
          <label htmlFor="email" className="eyebrow mb-2 block">
            Email
          </label>
          <input
            id="email"
            type="email"
            className="field"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />
        </div>

        {isSignup && (
          <div>
            <label htmlFor="phone" className="eyebrow mb-2 block">
              Mobile number
            </label>
            <input
              id="phone"
              type="tel"
              className="field"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              autoComplete="tel"
            />
            <p className="mt-2 text-xs text-muted">
              Helps us match you to the guest list, and lets us text you weekend details. We won't
              share it.
            </p>
          </div>
        )}

        {error && <Notice tone="error">{error}</Notice>}

        <button type="submit" className="btn btn-primary w-full" disabled={busy}>
          {busy ? "Sending…" : "Email me a sign-in link"}
        </button>
      </form>

      <p className="mt-8 text-center text-sm text-muted">
        {isSignup ? (
          <>
            Already signed up?{" "}
            <a {...linkProps("/login")} className="text-ink underline decoration-line underline-offset-4">
              Sign in
            </a>
          </>
        ) : (
          <>
            First time here?{" "}
            <a {...linkProps("/signup")} className="text-ink underline decoration-line underline-offset-4">
              Start your RSVP
            </a>
          </>
        )}
      </p>

      <p className="mt-10 text-center text-xs text-muted">
        Trouble signing in? Email us at{" "}
        <a href={`mailto:${site.contactEmail}`} className="underline underline-offset-4">
          {site.contactEmail}
        </a>
      </p>
    </div>
  );
}

export const SignUp = () => (
  <NarrowPage>
    <AuthForm mode="signup" />
  </NarrowPage>
);

export const Login = () => (
  <NarrowPage>
    <AuthForm mode="login" />
  </NarrowPage>
);
